import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Check, Loader2, RotateCcw, Music2 } from "lucide-react";
import { MusicPicker } from "@/components/shorts/MusicPicker";
import { api, formatApiError } from "@/lib/api";
import { uploadToCloudinary, clipToFile } from "@/lib/media";
import { filterCss } from "@/lib/filters";
import { CaptureStep } from "@/components/shorts/CaptureStep";
import { EditTools } from "@/components/shorts/EditTools";
import { PublishPanel } from "@/components/shorts/PublishPanel";
import { MediaOverlays } from "@/components/shorts/Overlays";
import { BlurBackdrop } from "@/components/shorts/BlurBackdrop";

const EMPTY_EDITS = { filter: "none", texts: [], stickers: [] };
const STAGES = { text: "A verificar o texto com o Guarda-Costas IA…", upload: "A enviar para a nuvem…", visual: "A analisar imagens com IA…", audio: "A transcrever o áudio (Whisper)…", guard: "A analisar com IA (Guarda-Costas)…", done: "A publicar no feed…" };

function Pipeline({ stage, photo, pct }) {
  const keys = Object.keys(STAGES).filter((k) => !(photo && k === "audio"));
  const cur = keys.indexOf(stage);
  return (
    <div className="space-y-4 rounded-2xl bg-white/5 p-5" data-testid="publish-pipeline">
      <p className="flex items-center gap-2 font-bold" data-testid="publish-loading-label"><Loader2 size={18} className="animate-spin text-[hsl(var(--gold))]" /> {STAGES[stage]}{stage === "upload" ? ` ${pct}%` : ""}</p>
      {stage === "upload" && <div className="h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full bg-[hsl(var(--gold))] transition-[width]" style={{ width: `${pct}%` }} data-testid="upload-progress-bar" /></div>}
      <ul className="space-y-2.5">
        {keys.map((k, i) => (
          <li key={k} className={`flex items-center gap-3 text-sm ${i <= cur ? "text-white" : "text-muted-foreground"}`} data-testid={`publish-stage-${k}`}>
            {i < cur ? <Check size={16} className="text-emerald-400" /> : i === cur ? <Loader2 size={16} className="animate-spin text-[hsl(var(--gold))]" /> : <span className="h-4 w-4 rounded-full border border-white/20" />}
            {STAGES[k].replace("…", "")}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MusicPreview({ music }) {
  const ref = useRef(null);
  const src = music.track.preview_url.replace(/so_\d+,du_\d+/, `so_${music.start},du_60`);
  useEffect(() => { if (ref.current) { ref.current.volume = music.volume; ref.current.play().catch(() => {}); } }, [src, music.volume]);
  return (
    <>
      <audio ref={ref} src={src} loop data-testid="preview-music-audio" />
      <div className="pointer-events-none absolute left-3 top-3 z-10 inline-flex max-w-[80%] items-center gap-1.5 rounded-full bg-black/60 px-3 py-1 text-xs font-bold backdrop-blur" data-testid="preview-music-chip">
        <Music2 size={13} className="text-[hsl(var(--gold))]" /> <span className="truncate">{music.track.title}</span>
      </div>
    </>
  );
}

function RecordedVideo({ clip, style }) {
  const [src, setSrc] = useState(clip.previewUrl);
  const [tries, setTries] = useState(0);
  useEffect(() => { setSrc(clip.previewUrl); setTries(0); }, [clip]);
  const onError = () => {
    if (tries > 0 || !clip.file) return setTries(2);
    setTries(1);
    setSrc(URL.createObjectURL(new Blob([clip.file], { type: clip.file.type || "video/mp4" })));
  };
  return (
    <>
      <video key={src} src={src} autoPlay loop muted playsInline controls={tries > 1} preload="auto" onLoadedData={(e) => e.currentTarget.play().catch(() => {})} onError={onError}
        style={style} className="absolute inset-0 h-full w-full bg-transparent object-contain" data-testid="recorded-preview" />
      {tries > 1 && <p className="pointer-events-none absolute inset-x-3 bottom-3 z-10 rounded-xl bg-black/70 p-2 text-center text-xs" data-testid="preview-unavailable-note">Prévia limitada neste navegador. Pode editar e publicar normalmente.</p>}
    </>
  );
}

function Preview({ clip, edits, setEdits, selected, setSelected, music }) {
  const move = ({ kind, idx }, x, y) => setEdits((e) => ({ ...e, [kind]: e[kind].map((it, i) => (i === idx ? { ...it, x, y } : it)) }));
  const style = { filter: filterCss(edits.filter) };
  return (
    <div className="relative h-full w-full overflow-hidden rounded-3xl bg-black" data-testid="edit-preview">
      {clip.type === "photo"
        ? <><BlurBackdrop src={clip.previewUrl} style={style} /><img src={clip.previewUrl} alt="Prévia" style={style} className="absolute inset-0 h-full w-full object-contain" data-testid="recorded-preview" /></>
        : <RecordedVideo clip={clip} style={style} />}
      <MediaOverlays edits={edits} editable selected={selected} onSelect={setSelected} onMove={move} />
      {music && <MusicPreview music={music} />}
    </div>
  );
}

export default function CreatePost() {
  const navigate = useNavigate();
  const [clip, setClip] = useState(null);
  const [edits, setEdits] = useState(EMPTY_EDITS);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ caption: "", destination_id: null });
  const [stage, setStage] = useState(null);
  const [media, setMedia] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [music, setMusic] = useState(null);
  const [blocked, setBlocked] = useState(null);

  const onClip = (c) => { setClip(c); setMedia(null); setBlocked(null); };

  const retake = () => { if (clip?.previewUrl?.startsWith("blob:")) URL.revokeObjectURL(clip.previewUrl); setClip(null); setMedia(null); setMusic(null); setBlocked(null); setEdits(EMPTY_EDITS); setSelected(null); };

  const block = (detail) => {
    setBlocked({ msg: detail.msg || detail.screening?.reason, checks: detail.screening?.checks });
    toast.error("Publicação bloqueada pelo Guarda-Costas IA", { description: detail.msg || detail.screening?.reason, id: "screening-block" });
  };

  const publish = async () => {
    setBlocked(null);
    try {
      setStage("text");
      const { data: pre } = await api.post("/shorts/screen", { caption: form.caption, transcript: "", media_type: clip.type, edits });
      if (!pre.allowed) { setStage(null); return block({ screening: pre }); }
      setStage("upload");
      setUploadPct(0);
      const m = media || await uploadToCloudinary(await clipToFile(clip), clip.type, setUploadPct);
      setMedia(m);
      setStage("visual");
      await api.post("/shorts/inspect", m);
      if (clip.type === "video") { setStage("audio"); await api.post("/shorts/transcribe", m); }
      setStage("guard");
      const { data } = await api.post("/shorts", { caption: form.caption, destination_id: form.destination_id, transcript: "", media_type: clip.type, edits, media: m, duration: Math.min(60, clip.duration || 0), music: music ? { track_id: music.track.id, start: music.start, volume: music.volume } : null });
      setStage("done");
      toast.success("Publicado!", { description: `Armazenado no Cloudinary (${data.storage.compressed_mb}MB)` });
      navigate(`/curtas?v=${data.id}`);
    } catch (e) {
      setStage(null);
      const detail = e?.response?.data?.detail;
      if (detail?.guard) { setMedia(null); block(detail); if (detail.blocked) navigate("/lives"); return; }
      toast.error(e?.response ? formatApiError(e) : (e.message || formatApiError(e)));
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="flex h-14 items-center justify-between px-4">
        <button onClick={() => navigate(-1)} className="grid h-10 w-10 place-items-center rounded-full hover:bg-white/10" data-testid="record-close-button"><X size={20} /></button>
        <h1 className="font-bold">{clip ? "Editar e publicar" : "Criar publicação"}</h1>
        {clip && !stage ? <button onClick={retake} className="grid h-10 w-10 place-items-center rounded-full hover:bg-white/10" data-testid="record-retake-button"><RotateCcw size={18} /></button> : <span className="w-10" />}
      </header>
      <div className="mx-auto grid max-w-5xl gap-6 px-4 pb-10 md:grid-cols-[360px_1fr]">
        <div className="mx-auto aspect-[9/16] w-full max-w-[360px]">
          {clip ? <Preview clip={clip} edits={edits} setEdits={setEdits} selected={selected} setSelected={setSelected} music={music} /> : <CaptureStep onDone={onClip} />}
        </div>
        <div className="space-y-5">
          {!clip && <p className="text-sm text-muted-foreground">Escolha Foto ou Vídeo (até 60s), inverta a câmera ou carregue da galeria. Antes de publicar, a mídia vai para a nuvem e o Guarda-Costas IA transcreve o áudio (Whisper) e verifica tudo automaticamente.</p>}
          {clip && !stage && <MusicPicker value={music} onChange={setMusic} />}
          {clip && !stage && <EditTools edits={edits} setEdits={setEdits} selected={selected} setSelected={setSelected} />}
          {clip && !stage && <PublishPanel form={form} setForm={setForm} onPublish={publish} blocked={blocked} />}
          {stage && <Pipeline stage={stage} photo={clip.type === "photo"} pct={uploadPct} />}
        </div>
      </div>
    </div>
  );
}
